import { useState } from "react";
import { Mail, Phone, MapPin, Send, Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { Button } from "../custom_ui/button.tsx";
import { Input } from "../custom_ui/input.tsx";
import { Textarea } from "../custom_ui/textarea.tsx";
import { useToast } from "../../hooks/use-toast.tsx";
import * as LucideIcons from "lucide-react";
import { submit_contact_form_api } from "../../controllers/dataController";

interface SocialLink {
  icon?: any;
  icon_name?: string;
  href?: string;
  url?: string;
  label?: string;
  platform?: string;
}

interface ContactSectionProps {
  content?: {
    contact_email?: string;
    contact_phone?: string;
    contact_address?: string;
  };
  socialLinks?: SocialLink[];
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

const defaultSocialLinks: SocialLink[] = [
  { icon: Facebook, href: "https://facebook.com/baajmedia", label: "Facebook" },
  { icon: Instagram, href: "https://instagram.com/baajmedia", label: "Instagram" },
  { icon: Twitter, href: "https://twitter.com/baajmedia", label: "Twitter" },
  { icon: Youtube, href: "https://youtube.com/baajmedia", label: "YouTube" },
];

export function ContactSection({ content, socialLinks }: ContactSectionProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submit_contact_form_api(formData);
      toast({
        title: "Message Sent!",
        description: "We'll get back to you within 24 hours.",
      });
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const displaySocialLinks = socialLinks && socialLinks.length > 0 ? socialLinks : defaultSocialLinks;

  return (
    <section id="contact" className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <p className="text-primary font-medium tracking-[0.2em] uppercase mb-4">
            Get In Touch
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-foreground mb-6">
            LET'S <span className="text-gradient-gold">CONNECT</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Ready to take your digital presence to the next level? Get in touch
            with us today.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div className="bg-gradient-card rounded-2xl p-8 md:p-10 border border-border/50 shadow-card">
            <h3 className="font-display text-2xl text-foreground mb-6">
              TELL US ABOUT YOUR PROJECT
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Input
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    className="bg-secondary border-border focus:border-primary h-12"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                    className="bg-secondary border-border focus:border-primary h-12"
                  />
                </div>
              </div>
              <div>
                <Input
                  type="tel"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="bg-secondary border-border focus:border-primary h-12"
                />
              </div>
              <div>
                <Textarea
                  placeholder="Tell us about your project..."
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  required
                  rows={5}
                  className="bg-secondary border-border focus:border-primary resize-none"
                />
              </div>
              <Button
                type="submit"
                variant="hero"
                size="xl"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    <Send size={20} />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-10">
            <div>
              <h3 className="font-display text-2xl text-foreground mb-6">
                CONTACT INFORMATION
              </h3>
              <div className="space-y-6">
                <a
                  href={`mailto:${content?.contact_email || "baajmediaofficial@gmail.com"}`}
                  className="flex items-center gap-4 text-muted-foreground hover:text-primary transition-colors group"
                >
                  <div className="w-12 h-12 rounded-lg bg-secondary group-hover:bg-gradient-gold flex items-center justify-center transition-all">
                    <Mail size={22} className="group-hover:text-primary-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="text-foreground font-medium">
                      {content?.contact_email || "baajmediaofficial@gmail.com"}
                    </p>
                  </div>
                </a>

                <a
                  href={`tel:${content?.contact_phone || "+919729090280"}`}
                  className="flex items-center gap-4 text-muted-foreground hover:text-primary transition-colors group"
                >
                  <div className="w-12 h-12 rounded-lg bg-secondary group-hover:bg-gradient-gold flex items-center justify-center transition-all">
                    <Phone size={22} className="group-hover:text-primary-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="text-foreground font-medium">{content?.contact_phone || "+91 9729090280"}</p>
                  </div>
                </a>

                <div className="flex items-center gap-4 text-muted-foreground ">
                  <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center">
                    <MapPin size={22} className="group-hover:text-primary-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="text-foreground font-medium">{content?.contact_address || "India"}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h3 className="font-display text-2xl text-foreground mb-6">
                FOLLOW US
              </h3>
              <div className="flex gap-4">
                {displaySocialLinks.map((social) => {
                  const iconName = social.icon_name || "";
                  // @ts-ignore
                  const IconComponent = social.icon_name ? LucideIcons[iconName] : (social.icon || LucideIcons.Link);

                  return (
                    <a
                      key={social.label || social.platform}
                      href={social.href || social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-lg bg-secondary hover:bg-gradient-gold flex items-center justify-center transition-all duration-300 group"
                      aria-label={social.label || social.platform}
                    >
                      {/* @ts-ignore */}
                      <IconComponent
                        size={22}
                        className="text-foreground group-hover:text-primary-foreground transition-colors"
                      />
                    </a>
                  );
                })}
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
